/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useState } from "react";
import { useAppCoffeeMugContext } from "../Components/AppCoffeeMugContext";
import IAppCoffeeMugContext from "../Components/AppCoffeeMugContext/IAppCoffeeMugContext";
import { fixedT, translationKeys } from "../Internationalisierung/i18nBasis";

export interface ViewModelArgs<TState> {
    initialState: TState;
}

type ChangeSource = number | string | boolean | null | {};

export default abstract class ViewModel<TState extends {}> {
    protected readonly _appCoffeeMug: IAppCoffeeMugContext;
    protected _initialState: TState;
    private readonly _setState: React.Dispatch<React.SetStateAction<TState>>;
    private readonly _state: TState;

    protected constructor(args: ViewModelArgs<TState>) {
        this._initialState = args.initialState;
        const [state, setState] = useState<TState>(args.initialState);
        this._state = state;
        this._setState = setState;
        this._appCoffeeMug = useAppCoffeeMugContext();
    }

    public aendereWertFuer = (propertyName: string) => (source: ChangeSource) => {
        let value: any = source;
        if (source && typeof source === "object" && "target" in (source as any)) {
            value = (source as any).target.value;
        }

        this.aendereWert(propertyName, value);
    }

    public get state(): TState {
        return this._state;
    }

    public aendereWert(propertyName: string, value: any): void {
        this.setState((nextState: any) => {
            nextState[propertyName] = value;
        });
    }

    protected setState(modifyState: (nextState: TState) => void) {
        this._setState((prevState: TState) => {
            const nextState: TState = { ...prevState };
            modifyState(nextState);
            return nextState;
        });
    }

    protected setInitialState(initialState: TState) {
        this._initialState = initialState;
    }

    protected reset() {
        this._setState(this._initialState);
    }

    protected async executeCommandAsync(action: () => Promise<void>): Promise<void> {
        try {
            this.showCoffeeMug();
            return await action();
        } finally {
            this.hideCoffeeMug();
        }
    }

    protected readonly showCoffeeMug = (message?: string) => {
        this._appCoffeeMug.show(message ?? fixedT(translationKeys.viewModel.showCoffeeMugText));
    }

    protected readonly hideCoffeeMug = () => {
        this._appCoffeeMug.hide();
    }
}
